// Onboarding step (create path only, docs/06 §2): pick a display name.
// Restored accounts keep their server-side name and skip this step entirely.
// Protocol limit is 64 chars (frontend/protocol `DisplayName`).

import { useState, type FormEvent, type ReactNode } from 'react';
import { useT } from '../../i18n/index.js';

const MAX_DISPLAY_NAME_LENGTH = 64;

export function DisplayName({
  onContinue,
  onBack,
}: {
  onContinue: (name: string) => void;
  /** Steps back to wherever this screen was reached from (the phrase quiz). */
  onBack?: () => void;
}): ReactNode {
  const t = useT();
  const [name, setName] = useState('');
  const trimmed = name.trim();
  const valid = trimmed.length >= 1 && trimmed.length <= MAX_DISPLAY_NAME_LENGTH;

  function handleSubmit(e: FormEvent): void {
    e.preventDefault();
    if (!valid) return;
    onContinue(trimmed);
  }

  return (
    <main className="mx-auto flex min-h-full w-full max-w-app flex-col gap-6 p-6">
      <h1 className="text-2xl font-semibold text-ink">{t('displayName.title')}</h1>
      <p className="text-sm text-muted">
        {t('displayName.help')}
      </p>
      <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
        <label className="flex flex-col gap-1 text-sm text-ink">
          {t('displayName.label')}
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={MAX_DISPLAY_NAME_LENGTH}
            autoComplete="name"
            className="min-h-touch rounded-xl border border-edge bg-surface px-3 text-ink"
          />
        </label>
        <div className="flex gap-3">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="min-h-touch flex-1 rounded-xl border border-edge bg-surface px-4 font-medium text-ink"
            >
              {t('common.back')}
            </button>
          )}
          <button
            type="submit"
            disabled={!valid}
            className="min-h-touch flex-1 rounded-xl bg-accent px-4 font-medium text-ink disabled:opacity-40 active:bg-accent-press"
          >
            {t('common.continue')}
          </button>
        </div>
      </form>
    </main>
  );
}
